import { api } from './client';

export type DoStage =
  | 'active'
  | 'credit-pending'
  | 'credit-approved'
  | 'credit-rejected'
  | 'fulfilled'
  | 'cancelled';

export type DoStatus = 'Active' | 'Fulfilled' | 'Cancelled';

export interface DoItem {
  id: number;
  itemId: number | null;
  itemCode: string | null;
  itemName: string;
  hsn: string | null;
  gstPct: string;
  qty: string;
  unit: string;
  rate: string;
  amount: string;
  imageUrl?: string | null;
  barcode?: string | null;
}

export interface DispatchOrder {
  id: number;
  doNo: string;
  doDate: string;
  enqId: number;
  enquiry?: { id: number; enqNo: string; remarks?: string | null };
  partyId: number;
  party: { id: number; name: string; customerType: string | null; creditLimit?: string | null; outstanding?: string };
  deliveryAddress: string;
  expectedDispatch: string;
  salesPerson: string | null;
  stage: DoStage;
  status: DoStatus;
  isPartial: boolean;
  totalValue: string;
  creditStatus: 'GREEN' | 'AMBER' | 'RED' | null;
  creditNote: string | null;
  remarks: string | null;
  items: DoItem[];
  pickLists?: { id: number; pickNo: string; status: string }[];
}

export interface DoFilter {
  stage?: string;
  status?: string;
  partyId?: number;
  from?: string;
  to?: string;
  search?: string;
  channel?: string;
  partial?: 'yes' | 'no';
  page?: number;
  pageSize?: number;
}

export interface DoListResult {
  data: DispatchOrder[];
  total: number;
  page: number;
  pageSize: number;
}

export async function listDos(filter: DoFilter = {}): Promise<DoListResult> {
  const r = await api.get<DoListResult>('/do', { params: filter });
  return r.data;
}

export async function creditQueue(): Promise<DispatchOrder[]> {
  const r = await api.get<DispatchOrder[]>('/do/credit-queue');
  return r.data;
}

export async function getDo(id: number): Promise<DispatchOrder> {
  const r = await api.get<DispatchOrder>(`/do/${id}`);
  return r.data;
}

export async function promoteEnquiry(payload: {
  enqId: number;
  expectedDispatch: string;
  deliveryAddress?: string;
  remarks?: string;
  salesPerson?: string;
  allowPartial?: boolean;
}): Promise<DispatchOrder> {
  const r = await api.post<DispatchOrder>('/do/promote-enquiry', payload);
  return r.data;
}

export async function approveCreditOverride(id: number, note: string): Promise<DispatchOrder> {
  const r = await api.post<DispatchOrder>(`/do/${id}/credit-approve`, { note });
  return r.data;
}

export async function blockCredit(id: number, reason: string): Promise<DispatchOrder> {
  const r = await api.post<DispatchOrder>(`/do/${id}/credit-block`, { reason });
  return r.data;
}

export async function cancelDo(id: number, reason: string): Promise<DispatchOrder> {
  const r = await api.post<DispatchOrder>(`/do/${id}/cancel`, { reason });
  return r.data;
}

export function dosExportPath(filter: DoFilter = {}): string {
  const search = new URLSearchParams(
    Object.fromEntries(
      Object.entries(filter).filter(([, v]) => v != null && v !== ''),
    ) as Record<string, string>,
  ).toString();
  return `/do/export.xlsx${search ? `?${search}` : ''}`;
}
